import { useCallback } from "react";
import { ICreatorsItems, INichesItems, ITagsList } from "@/common/services/types";
import { HighlightedText } from "@/components/UI/HighlightedText";
import { TagCard } from "../TagCard";
import { ProfileCard } from "../ProfileCard";
import { NicheCard } from "../NicheCard";
import { useSearchResult } from "./hooks/useSearchResult";
import { useSfw } from "@/common/hooks/useSfw";

type TSearchResultsType = INichesItems | ICreatorsItems | ITagsList;

interface ISearchResultItem {
  data: TSearchResultsType;
  searchValue?: string;
}

export const SearchResultItem = ({ data, searchValue }: ISearchResultItem) => {
  const { handleClick } = useSearchResult();
  const { sanitizeImage } = useSfw();

  const getHighlightedText = useCallback((text: string) => {
    return searchValue ? <HighlightedText highlight={searchValue} text={text} /> : undefined;
  }, [searchValue]);

  switch (data.type) {
  case "tag": {
    const { text, gifs } = data as ITagsList;
    return (
      <TagCard
        tagName={text}
        qty={gifs}
        highlightText={getHighlightedText(text)}
        onClick={() => handleClick({ historyType: "tag", historyValue: text, metricType: "Tag", metricValue: text })} />
    );
  }
  case "creator": {
    const { followers, image, name, studio, verified, userId } = data as ICreatorsItems;
    const sanitizedAvatar = image ? sanitizeImage(image) : null;

    return (
      <ProfileCard
        verified={verified}
        followers={followers}
        userName={name}
        profileImageUrl={sanitizedAvatar}
        isStudio={studio}
        highlightText={getHighlightedText(name)}
        userId={userId}
        hasSubscribeMsg
        onClick={() => handleClick({ historyType: "user", historyValue: userId, metricType: "User", metricValue: name })}
      />
    );
  }
  case "niche": {
    const { image, subscribers, text, id } = data as INichesItems;
    const sanitizedNicheImageUrl = image ? sanitizeImage(image) : null;

    return (
      <NicheCard
        id={id}
        nicheImageUrl={sanitizedNicheImageUrl}
        followers={subscribers}
        nicheName={text}
        highlightText={getHighlightedText(text)}
        onClick={() => handleClick({ historyType: "niche", historyValue: id, metricType: "Niche", metricValue: text })}
      />
    );
  }
  default: {
    return null;
  }
  }
};
